<template>
	<div class="label-manager">
		<v-snackbar v-model="snackbar" :color="snackbarColor">
			{{ msg }}
			<template #actions>
				<v-btn variant="text" color="white" @click="snackbar = false">
					{{ $t('action_close') }}
				</v-btn>
			</template>
		</v-snackbar>

		<div class="labels-display">
			<template v-if="selectedLabels.length > 0 || typing">
				<v-chip
					v-for="label in selectedLabels"
					:key="label.id"
					closable
					close-icon="mdi-close"
					size="small"
					class="ma-1"
					:class="{ 'label-chip-disabled': !edit }"
					@click:close="removeLabel(label)"
				>
					{{ label.name }}
				</v-chip>

				<v-combobox
					v-if="typing"
					ref="labelInput"
					v-model="selectedLabel"
					v-model:search="searchInput"
					:items="filteredLabels"
					item-title="name"
					item-value="id"
					density="compact"
					hide-no-data
					return-object
					variant="underlined"
					class="inline-input"
					color="primary"
					hide-details
					clear-on-blur
					@update:modelValue="handleSelection"
					@keydown.enter="handleEnterKey"
					@blur="onBlur"
					:menu-props="{ offsetY: true, contentClass: 'custom-menu-list' }"
				>
					<template #item="{ props, item }">
						<v-list-item v-bind="props">
							<template #title>
								<span :class="{ 'disabled-label': item.raw.disabled }">
									{{ item.raw.displayName || item.raw.name }}
								</span>
							</template>
						</v-list-item>
					</template>
				</v-combobox>

				<v-btn v-if="!typing" icon="mdi-plus" variant="text" size="small" class="add-label-btn" :disabled="!edit" @click="startTyping" />
			</template>

			<v-btn v-else variant="text" size="small" color="primary" :disabled="!edit" @click="startTyping" class="add-text-btn">
				{{ $t('action_add') }}
			</v-btn>
		</div>
	</div>
</template>

<script>
import i18n from '@/plugins/i18n';
import { debounce } from 'lodash';
import axios from 'axios';
import { nextTick } from 'vue';

export default {
	name: 'LabelManager',
	// Vue 3 explicit emits
	emits: ['update:modelValue', 'error'],
	props: {
		// Vue 3 default v-model prop name
		modelValue: {
			type: Array,
			default: () => [],
		},
		edit: {
			type: Boolean,
			default: false,
		},
		debounceDelay: {
			type: Number,
			default: 300,
		},
	},
	data() {
		return {
			typing: false,
			selectedLabel: null,
			searchInput: '',
			loading: false,
			availableLabels: [],
			snackbar: false,
			snackbarColor: '',
			msg: '',
			hasSearched: false,
		};
	},
	computed: {
		selectedLabels() {
			return this.modelValue || [];
		},
		filteredLabels() {
			const selectedIds = this.selectedLabels.map((l) => l.id);
			const selectedNames = this.selectedLabels.map((l) => l.name.toLowerCase());
			const filtered = this.availableLabels.filter((label) => !selectedIds.includes(label.id));

			if (!this.hasSearched && this.loading) return [];
			if (!this.searchInput?.trim()) return filtered;

			const searchLower = this.searchInput.toLowerCase().trim();
			const isAlreadyAdded = selectedNames.includes(searchLower);

			if (this.hasSearched && !this.loading && filtered.length === 0) {
				if (isAlreadyAdded) {
					return [
						{
							id: 'already-added',
							name: this.searchInput.trim(),
							displayName: `${this.searchInput.trim()} ${this.$t('label.already_added')}`,
							disabled: true,
						},
					];
				}
				return this.buildNoResultItems();
			}

			const exactMatch = filtered.find((l) => l.name.toLowerCase() === searchLower);

			if (!exactMatch && isAlreadyAdded) {
				return [
					{
						id: 'already-added',
						name: this.searchInput.trim(),
						displayName: `${this.searchInput.trim()} ${this.$t('label.already_added')}`,
						disabled: true,
					},
					...filtered,
				];
			}

			if (!exactMatch && this.hasSearched) {
				return this.buildCreateNewItems(filtered);
			}

			return filtered;
		},
	},
	methods: {
		async startTyping() {
			this.typing = true;
			await nextTick();
			// In Vuetify 3, use refs for cleaner focus
			this.$refs.labelInput?.focus();
		},
		stopTyping() {
			this.typing = false;
			this.searchInput = '';
			this.selectedLabel = null;
			this.availableLabels = [];
			this.hasSearched = false;
		},
		onBlur(event) {
			// Check if the click occurred inside the dropdown menu items
			const isClickInsideMenu = event.relatedTarget && event.relatedTarget.closest('.custom-menu-list');

			if (isClickInsideMenu) {
				return;
			}

			setTimeout(() => {
				this.stopTyping();
			}, 50);
		},
		buildNoResultItems() {
			return [
				{
					id: 'none',
					name: this.searchInput.trim(),
					displayName: this.$t('label.no_labels_found'),
					disabled: true,
				},
				{
					id: 'new',
					name: this.searchInput.trim(),
					displayName: `${this.searchInput.trim()} ${this.$t('label.create_new')}`,
					isNew: true,
				},
			];
		},
		async handleEnterKey() {
			const query = this.searchInput?.trim();
			if (!query) return;

			const selectedNames = this.selectedLabels.map((l) => l.name.toLowerCase());

			if (selectedNames.includes(query.toLowerCase())) {
				this.stopTyping();
				return;
			}

			await this.createLabel(query);
			await nextTick();
			this.stopTyping();
		},
		buildCreateNewItems(filtered) {
			return [
				{
					id: 'new',
					name: this.searchInput.trim(),
					displayName: `${this.searchInput.trim()} ${this.$t('label.create_new')}`,
					isNew: true,
				},
				...filtered,
			];
		},
		removeLabel(label) {
			// Assumed _id or id based on your mix in original code
			const updated = this.selectedLabels.filter((l) => (l.id || l._id) !== (label.id || label._id));
			this.$emit('update:modelValue', updated);
		},
		async handleSelection(item) {
			if (!item || item.disabled || typeof item === 'string') return;

			if (item.isNew) {
				await this.createLabel(item.name);
			} else {
				this.addLabel(item);
			}
			await nextTick();
			this.stopTyping();
		},
		addLabel(label) {
			if (!label || this.selectedLabels.some((l) => l.id === label.id)) return;
			this.$emit('update:modelValue', [...this.selectedLabels, label]);
			this.resetMenu();
		},
		async createLabel(labelName) {
			if (!labelName?.trim()) return;
			try {
				const response = await axios.post('/api/labels', { name: labelName.trim() });
				this.addLabel(response.data);
			} catch (error) {
				this.msg = this.$t(error.response?.data?.message || 'Error');
				this.snackbarColor = 'error';
				this.snackbar = true;
			}
		},
		async searchLabels(query) {
			if (!query?.trim()) {
				this.availableLabels = [];
				this.hasSearched = false;
				return;
			}
			this.loading = true;
			try {
				const response = await axios.get('/api/labels', { params: { name: query.trim() } });
				this.availableLabels = response.data || [];
				this.hasSearched = true;
			} catch (error) {
				this.availableLabels = [];
				this.$emit('error', { type: 'search', error });
				this.hasSearched = true;
			} finally {
				this.loading = false;
			}
		},
		resetMenu() {
			this.selectedLabel = null;
			this.searchInput = '';
			this.availableLabels = [];
			this.hasSearched = false;
		},
	},
	watch: {
		searchInput(val) {
			if (val?.trim()) this.hasSearched = false;
			this.debouncedSearch?.(val);
		},
	},
	created() {
		this.debouncedSearch = debounce(this.searchLabels, this.debounceDelay);
	},
	beforeUnmount() {
		this.debouncedSearch?.cancel();
	},
};
</script>

<style scoped>
.label-manager {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	background-color: #fff;
	padding: 0 0;
	margin-top: 4px;
}
.labels-display {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 4px;
	padding: 0 10px;
	min-height: 40px;
}
.inline-input {
	width: 200px !important;
	max-width: 200px !important;
}
.disabled-label {
	color: grey;
	pointer-events: none;
}
.custom-menu-list {
	border-radius: 8px !important;
	box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1) !important;
	margin-top: 4px;
}
.label-chip-disabled :deep(.v-chip__close) {
	opacity: 0.4;
	pointer-events: none;
}
</style>
